import React, { useEffect, useState } from 'react';

import { LoadingSpinner } from '@components';
import { LastMetricsPeriod } from '@interfaces';
import { SimcardService } from '@services';
import { RootState, setMetricGroups } from '@stores';
import { transformToGrouped } from '@utils';
import { useDispatch, useSelector } from 'react-redux';
import { useAside } from '../../../hooks/useAside';
import { SimMetricsGroupedMockData } from '../../../mock';
import MetricGroupBreakdownCard from './MetricGroupBreakdown';

type Props = {
  phoneNumbers?: string[];
};

type Category = 'more' | 'mpay' | 'subscriptions' | 'all';
type MetricGroup =
  | 'calls'
  | 'data'
  | 'sms'
  | 'mpay'
  | 'subscriptions'
  | 'others';

const categoryMap: Record<Category, MetricGroup[]> = {
  more: ['calls', 'data', 'sms'],
  mpay: ['others'],
  subscriptions: ['subscriptions'],
  all: ['calls', 'data', 'sms', 'subscriptions', 'mpay'],
};

export const SimMetricsGroupsPie = ({ phoneNumbers }: Props) => {
  const data = useSelector((state: RootState) => state.simBruto);
  const companyId = useSelector(
    (state: RootState) => state.companies.selectedCompany?.id
  );
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState<string>('');
  const sim = new SimcardService();
  const dispatch = useDispatch();
  const { openAside } = useAside();

  if (!companyId) {
    return;
  }

  useEffect(() => {
    const getSimData = async () => {
      setLoading(true);
      dispatch(setMetricGroups({}));

      try {
        if (companyId === 1) {
          const currentMonth = 8;
          const currentYear = 2026;

          const filtered = SimMetricsGroupedMockData.results.filter(
            (item) => item.month === currentMonth && item.year === currentYear
          );

          const grouped = transformToGrouped(filtered);

          dispatch(setMetricGroups(grouped));
          return;
        }

        const latestMetrics: LastMetricsPeriod =
          await sim.getLastMetricsMonth(companyId);

        const simData = await sim.getBruto(
          {
            months: [latestMetrics.month],
            metricGroups: [
              'calls',
              'data',
              'mpay',
              'others',
              'sms',
              'subscriptions',
            ],
            years: [latestMetrics.year],
            phoneNumbers,
          },
          companyId
        );

        if (simData.errorCode) {
          dispatch(setMetricGroups({}));
          console.error('Error in SIM data response:', simData.errorCode);
        } else {
          const grouped = transformToGrouped(simData.results);
          dispatch(setMetricGroups(grouped));
        }
      } catch (err) {
        console.error('Error fetching SIM data:', err);
      } finally {
        setLoading(false);
      }
    };

    getSimData();
  }, [phoneNumbers, companyId]);

  const onBreakdownClick = async (payload: {
    year: number;
    month: number;
    metricGroup: Category;
    phoneNumbers: string;
  }) => {
    setCategoryLoading(payload.metricGroup);
    const detail = await sim.getMetricsDetails(
      {
        // unit: 'bruto',
        years: [payload.year],
        months: [payload.month],
        metricGroups: categoryMap[payload.metricGroup],
        units: ['bruto', 'subscription'],
        // phoneNumbers: payload.phoneNumbers,
        groupBy: [
          'metrics.metric_name',
          'metrics.metric_group',
          'sims.phone_number',
          'metrics.metric_sub_division',
          'metrics.metric_unit',
        ],
        limit: 200,
        offset: 0,
      },
      companyId
    );

    const grouped = detail.results.reduce((acc, item) => {
      const key = item.phone_number;

      if (!acc[key]) {
        acc[key] = {
          phone_number: key,
          metrics: [],
        };
      }

      acc[key].metrics.push({
        metric_name: item.metric_name,
        group: item.metric_group,
        unit: item.metric_unit,
        subdev: item.metric_sub_division,
        value: item.value,
      });

      return acc;
    }, {});

    openAside({
      component: 'simMetricsContainer',
      data: {
        metrics: grouped,
        period: {
          year: payload.year,
          month: payload.month,
        },
      },
      title: 'Sim Metrics',
    });
    // setCategoryLoading('');
  };
  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : data.simBruto && Object.values(data.simBruto).length > 0 ? (
        <MetricGroupBreakdownCard
          data={data.simBruto}
          onClick={onBreakdownClick}
        />
      ) : (
        <p>Geen data</p>
      )}
    </>
  );
};

export default SimMetricsGroupsPie;
